import { Box, Sheet, Typography } from '@mui/joy';
import { FC, JSX, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Loader, PageBreadcrumbs } from '@consolidocs-ui/components';
import { useGetCompaniesQuery } from '@consolidocs-ui/data-access/CompanyApi';
import { CompanyType } from '@consolidocs-ui/types';
import CompanyForm from './CompanyForm';

/**
 * Full-page Edit Organization screen. Loads the organization from the shared
 * companies list (there is no single-company endpoint yet) and hydrates the
 * wizard form.
 */
const EditCompanyPage: FC = (): JSX.Element => {
  const navigate = useNavigate();
  const { companyId } = useParams<{ companyId: string }>();
  const { data: companies = [], isLoading } = useGetCompaniesQuery();

  const editData = useMemo<CompanyType | undefined>(
    () =>
      (companies as CompanyType[]).find(
        (c: CompanyType) => c.id === companyId
      ),
    [companies, companyId]
  );

  const goToList = () => navigate('/');

  return (
    <Box sx={{ flex: 1, width: '100%' }}>
      {/* Breadcrumbs pinned to the top of the viewport, using the same
          grey as `main-content` so the scrolling form slides underneath
          without a visible white bar. */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 9995,
          bgcolor: '#f5f5f5',
          // Cancel the .content wrapper's top padding (p-4) so the bar sticks
          // flush to the top instead of drifting down before it engages.
          mt: { md: -2 },
          pt: { xs: 1, md: 2 },
          pb: 1,
        }}
      >
        <PageBreadcrumbs
          items={[
            { label: 'Companies', path: '/' },
            { label: 'Edit Organization' },
          ]}
        />
      </Box>

      <Sheet
        variant="outlined"
        sx={{
          width: { xs: '100%', sm: '95%', md: '900px' },
          maxWidth: '900px',
          mx: 'auto',
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 'md',
          boxShadow: 'sm',
          borderColor: 'neutral.outlinedBorder',
          marginTop: { xs: '12px', md: '20px' },
        }}
      >
        <Typography level="h4" mb={2}>
          Edit Organization
        </Typography>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <Loader />
          </Box>
        ) : !editData ? (
          <Typography level="body-md" color="danger" sx={{ py: 4 }}>
            Organization not found. It may have been deleted.
          </Typography>
        ) : (
          <CompanyForm
            editData={editData}
            onCancel={goToList}
            onSuccess={goToList}
          />
        )}
      </Sheet>
    </Box>
  );
};

export default EditCompanyPage;
